/**
 * src/api/skills.js
 *
 * All API calls related to skills.
 * Bearer token is attached automatically from the auth cookie.
 */

import { authedJson } from './http'

const apiGet = path => authedJson('GET', path)
const apiPost = (path, body) => authedJson('POST', path, body)
const apiPatch = (path, body) => authedJson('PATCH', path, body)
const apiDelete = path => authedJson('DELETE', path)

/**
 * Fetch the active-skills coverage list.
 * GET /skills/coverage
 * @returns {Promise<{ skills: Array<{
 *   id: number, name: string, type: string, description: string|null,
 *   agent_count: number, target_agent_count: number,
 *   coverage_status: 'Insufficient'|'Neutral'|'Adequate'|'Strong',
 *   recommendation: string
 * }> }>}
 */
export async function fetchSkillsCoverage() {
  return apiGet('/skills/coverage')
}

/**
 * Fetch the tenant's active skill catalog (id/name/type). Used to power the
 * Knowledge Base tag filter and tag colors (no coverage stats needed there).
 * GET /skills/active
 * @returns {Promise<{ skills: Array<{ id: number, name: string, type: string }> }>}
 */
export async function fetchActiveSkills() {
  return apiGet('/skills/active')
}

/**
 * Fetch one active skill in coverage shape (same fields as an entry of
 * fetchSkillsCoverage). Used to refresh a single card after the Skill Details
 * modal changes who holds the skill.
 * GET /skills/{skillId}/coverage
 * @param {number|string} skillId
 * @returns {Promise<{
 *   id: number, name: string, type: string, description: string|null,
 *   agent_count: number, target_agent_count: number,
 *   coverage_status: 'Insufficient'|'Neutral'|'Adequate'|'Strong',
 *   recommendation: string
 * }>}
 */
export async function fetchSkillCoverage(skillId) {
  return apiGet(`/skills/${skillId}/coverage`)
}

/**
 * Fetch normalized, proposed, and rejected skill candidates (one card per skill).
 * Rows with status `new` are intentionally not exposed by the backend.
 * GET /skills/candidates
 * @returns {Promise<{ skills: Array<{
 *   id: number, skill_name: string, status: 'normalized'|'proposed'|'rejected',
 *   description: string|null,
 *   tickets: Record<string, string>, rejected_reason: string|null,
 *   reviewed_by: string|null, reviewed_at: string|null
 * }> }>}
 */
export async function fetchSkillCandidates() {
  return apiGet('/skills/candidates')
}

/**
 * Fetch the valid skill types for the Add Skill Manually picker.
 * GET /skills/types
 * @returns {Promise<{ types: string[] }>}
 */
export async function fetchSkillTypes() {
  return apiGet('/skills/types')
}

/**
 * Create a user-defined skill (Add Skill Manually). The backend writes it to both
 * Postgres and the graph and returns the new skill in coverage shape, ready to drop
 * into the Skills Coverage list.
 * POST /skills/new
 * @param {{ skillName: string, skillType: string }} fields
 * @returns {Promise<{
 *   id: number, name: string, type: string,
 *   agent_count: number, target_agent_count: number,
 *   coverage_status: 'Insufficient'|'Neutral'|'Adequate'|'Strong',
 *   recommendation: string
 * }>}
 */
export async function createSkill({ skillName, skillType }) {
  return apiPost('/skills/new', { skill_name: skillName, skill_type: skillType })
}

/**
 * Rename and/or reclassify an existing skill. The backend updates both Postgres and
 * the graph (propagating a rename to the Skill node) and returns the updated identity.
 * PATCH /skills/{skillId}
 * @param {number|string} skillId
 * @param {{ skillName: string, skillType: string, description?: string|null,
 *           targetAgentCount?: number }} fields
 *   targetAgentCount overrides the skill's optimal coverage; omit it to leave it unchanged.
 * @returns {Promise<{ id: number, name: string, type: string, description: string|null,
 *                     target_agent_count: number }>}
 */
export async function updateSkill(skillId, { skillName, skillType, description, targetAgentCount }) {
  return apiPatch(`/skills/${skillId}`, {
    skill_name: skillName,
    skill_type: skillType,
    description,
    // Only present when the optimal-coverage override was changed; omitted otherwise so
    // the server leaves the stored value alone (last save wins when it is sent).
    ...(targetAgentCount !== undefined && { target_agent_count: targetAgentCount }),
  })
}

/**
 * Approve, reject, or merge a skill candidate. The backend's SkillDecision body is
 * nested under the chosen action; this wraps the flat caller-supplied fields into it.
 * POST /skills/{action}/{skill_id}
 *   - approve → { approve: { skill_type: 'Core'|'Technical'|'General' } }; returns the
 *     new skill in coverage shape (id, name, type, agent_count, target_agent_count,
 *     coverage_status, recommendation). Associated tickets are resolved server-side at
 *     approval time, so none are sent.
 *   - reject  → { reject: { reason: string|null } }; reason is optional.
 *   - merge   → { merge: { skill_id: number } }; any reviewable candidate status.
 * @param {number|string} candidateId — skills_candidates.candidate_id (path {skill_id})
 * @param {'approve'|'reject'|'merge'} action — path {action}
 * @param {{ skillType?: string, reason?: string|null, targetSkillId?: number }} [fields]
 */
export async function submitSkillDecision(candidateId, action, { skillType, reason, targetSkillId } = {}) {
  const body = action === 'approve'
    ? { approve: { skill_type: skillType } }
    : action === 'merge'
      ? { merge: { skill_id: targetSkillId } }
      : { reject: { reason: reason ?? null } }
  return apiPost(`/skills/${action}/${candidateId}`, body)
}

/**
 * Delete a skill outright. The backend removes it from the skills table (and from
 * every team member who held it) and records it as rejected in the candidate store so
 * it won't be proposed again.
 * DELETE /skills/{skillId}
 * @param {number|string} skillId
 * @returns {Promise<{ status: 'deleted', skill_id: number }>}
 */
export async function deleteSkill(skillId) {
  return apiDelete(`/skills/${skillId}`)
}

/**
 * Fetch the mini-metric cards for the Skill Details modal (up to five).
 * GET /skills/{skillId}/metrics
 * @param {number|string} skillId
 * @returns {Promise<Array<{
 *   name: string, value: number|string, description: string,
 *   cardtype: 'number'|'bar', icon: string,
 *   evidence_header?: string, evidence?: string
 * }>>}
 */
export async function fetchSkillMetrics(skillId) {
  return apiGet(`/skills/${skillId}/metrics`)
}

/**
 * Fetch the team members who hold a given skill, for the Details modal table.
 * GET /skills/{skillId}/agents
 * @param {number|string} skillId
 * @returns {Promise<{ agents: Array<{
 *   agent_id: number, name: string, initials: string, level: string,
 *   count: number, last_used: string, locked: boolean
 * }> }>}
 */
export async function fetchSkillAgents(skillId) {
  return apiGet(`/skills/${skillId}/agents`)
}

/**
 * Fetch the most-recent evidence-case ticket numbers for a skill, shown as
 * CaseBadges, plus the total count of all matching cases.
 * GET /skills/{skillId}/evidence?limit={limit}
 * @param {number|string} skillId
 * @param {number} [limit=40] — max cases returned (backend caps at 500)
 * @returns {Promise<{ evidence: string[], total: number }>}
 */
export async function fetchSkillEvidence(skillId, limit = 40) {
  return apiGet(`/skills/${skillId}/evidence?limit=${limit}`)
}

/**
 * Fetch AI-generated evidence for one ticket backing a reviewable skill candidate
 * (the Proposed / Candidates / Rejected panel). Candidates live in Postgres only —
 * they get no Neo4j presence until approved/merged — so this is a distinct source
 * from fetchTicketEvidence (api/team.js), which the Skills Coverage list's
 * already-approved skills use instead. Symptoms/problems always come back empty;
 * only the candidate's own reason for this ticket is available.
 * GET /skills/candidates/{skillId}/evidence/{ticketId}
 * @param {number|string} skillId — skills_candidates.skill_id (the candidate id)
 * @param {number|string} ticketId
 * @returns {Promise<{ evidence: {
 *   ticket_number: string,
 *   resolving_agent: string|null,
 *   skills: Array<{ name: string, reason: string }>,
 *   symptoms: [], problems: [],
 * } }>}
 */
export async function fetchCandidateTicketEvidence(skillId, ticketId) {
  return apiGet(`/skills/candidates/${encodeURIComponent(skillId)}/evidence/${encodeURIComponent(ticketId)}`)
}
